import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import styled from "styled-components";
import { ReactNode } from "react";

const ItemWrapper = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const QuestionButton = styled(DisclosureButton)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 20px 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: "Sofia Pro", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.blue500};
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 767px) {
    font-size: 16px;
    padding: 16px 0;
  }
`;

const ChevronIcon = styled(ChevronDownIcon)<{ $isOpen: boolean }>`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
  margin-left: 16px;
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0)")};

  @media (max-width: 767px) {
    width: 20px;
    height: 20px;
  }
`;

const AnswerPanel = styled(DisclosurePanel)`
  padding: 0 0 20px 0;
  font-family: "Sofia Pro", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.blue500};

  p {
    margin: 0 0 12px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ol,
  ul {
    margin: 12px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 700;
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  @media (max-width: 767px) {
    font-size: 14px;
    line-height: 22px;
  }
`;

interface FAQItemProps {
  question: string;
  answer: ReactNode;
}

const FAQItem = ({ question, answer }: FAQItemProps) => {
  return (
    <Disclosure as={ItemWrapper}>
      {({ open }) => (
        <>
          <QuestionButton>
            <span>{question}</span>
            <ChevronIcon $isOpen={open} />
          </QuestionButton>
          <AnswerPanel>{answer}</AnswerPanel>
        </>
      )}
    </Disclosure>
  );
};

export default FAQItem;
